import { LIMIT } from '@/constants';
import {
  fetchAfterSaleByAfterSaleId,
  fetchAfterSaleByOrderId,
  fetchAfterSalesList,
  fetchGetRefundableAmount,
} from '@/services';
import {
  AfterSaleMode,
  AfterSaleState,
  LoadMoreStatus,
  LogisticsType,
  PromotionType,
  type AnyObj,
  type AnyObjOrNull,
} from '@/types';
import type { ShoppingTrolley } from '@/types/global-business';
import NP from 'number-precision';
import { defineStore } from 'pinia';

interface State {
  /** 需要售后的商品信息 */
  afterSaleCommodityList: ShoppingTrolley.Commodity[];
  // 批量退款商品
  refundSaleCommodityList: any[];
  // 选中的退款商品
  refundCheckedSaleCommdityList: any[];
  // 订单状态
  orderState: null;
  /** 售后提交数据 */
  afterSaleSumbits: {
    id: string;
    orderId: string;
    mode: string;
    cause: string;
    description: string;
    afterSaleLines: { lineId: string }[];
    img: string[];
    price: number;
  };
  /** 能申请的最大金额 */
  canApplyMaxPrice: number;
  /** 售后列表数据 */
  afterSaleList: AnyObj[];
  /** 列表加载状态 */
  loadStatus: LoadMoreStatus;
  /** 列表请求选项 */
  options: { skip: number };
  /** 售后中的列表数据 */
  beingAfterSaleList: AnyObj[];
  /** 售后详情数据 */
  afterSaleDetail: AnyObjOrNull;
  /** 修改物流标识 */
  modifyLogisticsFlag: boolean;
  /** 是否修改售后申请场景 */
  isModifyApplyScene: boolean;
  /** 购物金退款数据 */
  claimAndDeductionAmounts: {
    shoppingCreditsDeductionAmount: number;
    shoppingCreditsClaimAmount: number;
  };
}

export const useAfterSaleStore = defineStore('after-sale', {
  state: (): State => ({
    afterSaleCommodityList: [],
    refundSaleCommodityList: [], // 退款商品列表
    refundCheckedSaleCommdityList: [], //选中的退款商品
    orderState: null,
    afterSaleSumbits: {
      id: '',
      orderId: '',
      mode: AfterSaleMode.refundOnly,
      cause: '',
      description: '',
      afterSaleLines: [],
      img: [],
      price: 0,
    },
    canApplyMaxPrice: -1,
    afterSaleList: [],
    loadStatus: LoadMoreStatus.loading,
    options: { skip: 0 },
    beingAfterSaleList: [],
    afterSaleDetail: null,
    modifyLogisticsFlag: false,
    isModifyApplyScene: false,
    claimAndDeductionAmounts: {
      shoppingCreditsDeductionAmount: 0,
      shoppingCreditsClaimAmount: 0,
    },
  }),

  getters: {
    /** 物流数据 */
    logisticsData(state) {
      const detail = state.afterSaleDetail;

      if (!detail) return null;

      // 取用户物流
      if (detail.state === AfterSaleState.goodsToBeReceived) {
        return detail.logisticses.find((it: AnyObj) => it.type === LogisticsType.returnGoods);
      }

      // 取商家物流
      if (detail.state === AfterSaleState.confirmReceiptAndDelivery) {
        return detail.logisticses.find((it: AnyObj) => it.type === LogisticsType.deliverGoods);
      }

      return null;
    },
  },

  actions: {
    async getAfterSaleListByOrderId(orderId: string, refresh?: boolean) {
      if (!this.beingAfterSaleList.length || refresh) {
        this.beingAfterSaleList = await fetchAfterSaleByOrderId(orderId);
      }
      return this.beingAfterSaleList;
    },
    async getAfterSaleDetail(afterSaleId: string, refresh?: boolean) {
      if (!this.afterSaleDetail || refresh) {
        this.afterSaleDetail = await fetchAfterSaleByAfterSaleId(afterSaleId);
      }
      return this.afterSaleDetail;
    },
    async getAfterSaleList(skip: number = 0, refresh?: boolean) {
      const res = await fetchAfterSalesList(skip);
      if (res) {
        if (res.length < LIMIT) {
          this.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.afterSaleList = res;
        } else {
          this.afterSaleList = [...this.afterSaleList, ...res];
        }
      }
      return this.afterSaleList;
    },
    async getRefundableAmount(
      orderId: string,
      lineIds: string[],
      isInclusionShippingPrice = true,
      isUpdate = false
    ) {
      const data = await fetchGetRefundableAmount(
        orderId,
        lineIds,
        isInclusionShippingPrice,
        isUpdate
      );
      if (data >= 0) {
        this.canApplyMaxPrice = NP.divide(data, 100);
      }
    },
    /** 根据订单活动优惠信息补充盲盒相关字段 */
    replenishBlindBoxFieldsByOrderPromotionResult(
      list: AnyObject[],
      orderPromotionResult: AnyObjOrNull
    ) {
      // 活动数据
      const promLineResults = orderPromotionResult?.promResult?.promLineResults ?? [];
      // 找出盲盒的活动优惠信息
      const promLine = promLineResults.find((it: AnyObject) => it.type === PromotionType.BlindBox);

      console.log(`找出盲盒的活动优惠信息`, promLine);

      if (promLine?.orderLineBlindBoxMap) {
        const result = list.map((item) => {
          const relatedBlindBoxes = promLine.orderLineBlindBoxMap.filter(
            (box: AnyObject) => box.orderLineId === item.orderLine.id
          );
          const totalBlindBoxOrderBuyPrice = relatedBlindBoxes.reduce(
            (sum: number, box: AnyObject) => sum + box.blindBoxOrderBuyPrice,
            0
          );
          return {
            ...item,
            _blindBox: relatedBlindBoxes,
            _totalBlindBoxOrderBuyPrice: totalBlindBoxOrderBuyPrice,
          };
        });

        if (result.length > 0) {
          return result;
        }
      }

      return list;
    },
    resetSubmit() {
      this.canApplyMaxPrice = -1;
      this.isModifyApplyScene = false;
      this.afterSaleSumbits = {
        id: '',
        orderId: '',
        mode: AfterSaleMode.refundOnly,
        cause: '',
        description: '',
        afterSaleLines: [],
        img: [],
        price: 0,
      };
    },
  },
});
